import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CakeSlice,
  Citrus,
  Coffee,
  Croissant,
  GlassWater,
  Martini,
  Pizza,
  Popcorn,
  Salad,
  Sandwich,
  UtensilsCrossed,
  Wine,
  Sparkles,
  Info,
} from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { MENU } from "@/data/content";

const ICONS = {
  coffee: Coffee,
  croissant: Croissant,
  cake: CakeSlice,
  citrus: Citrus,
  sandwich: Sandwich,
  utensils: UtensilsCrossed,
  pizza: Pizza,
  salad: Salad,
  martini: Martini,
  glass: GlassWater,
  wine: Wine,
  popcorn: Popcorn,
};

const CATS = [
  { id: "colazione", label: "Colazioni & Dolci", testid: "menu-tab-colazione" },
  { id: "aperitivi", label: "Aperitivi", testid: "menu-tab-aperitivi" },
];

export const MenuSection = () => {
  const [tab, setTab] = useState("colazione");
  const items = MENU[tab];

  return (
    <section
      id="menu"
      className="scroll-mt-20 border-y border-sand/10 bg-roast py-20 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <Eyebrow>Il Menu</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Dal mattino a mezzanotte
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="inline-flex items-center gap-2 rounded-full border border-sand/20 px-4 py-2 text-sm text-cream/75">
              <Info className="size-4 text-sand" />
              {`1–10 € a persona · menu completo in loco`}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div className="mt-10 flex flex-wrap gap-2 border-b border-sand/15">
            {CATS.map((c) => (
              <button
                key={c.id}
                data-testid={c.testid}
                onClick={() => setTab(c.id)}
                className={`relative px-5 py-4 text-sm font-semibold transition-colors duration-300 ${
                  tab === c.id ? "text-cream" : "text-cream/50 hover:text-cream/80"
                }`}
              >
                {c.label}
                {tab === c.id && (
                  <motion.span
                    layoutId="menu-tab-underline"
                    className="absolute inset-x-4 bottom-0 h-0.5 bg-terra"
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  />
                )}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {items.map((item, i) => {
              const Icon = ICONS[item.icon] || Coffee;
              return (
                <motion.article
                  key={item.name}
                  data-testid={`menu-item-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  initial={{ opacity: 0, y: 22 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -5 }}
                  className="group rounded-2xl border border-sand/15 bg-mocha/60 p-6 transition-shadow duration-300 hover:border-terra/40 hover:shadow-[0_12px_40px_rgba(147,48,32,0.12)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex size-11 items-center justify-center rounded-full bg-terra/15 text-terra-light transition-colors duration-300 group-hover:bg-terra group-hover:text-cream">
                      <Icon className="size-5" />
                    </span>
                    {item.badge && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1 text-[11px] font-medium text-gold">
                        <Sparkles className="size-3" />
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-cream">
                    {item.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream/60">
                    {item.desc}
                  </p>
                </motion.article>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
